import { Link } from 'react-router-dom';
import { PageBody, PageHeader, ScopeBar } from '../components/AppShell';
import {
  Button,
  Card,
  CardHeader,
  Empty,
  ErrorState,
  LevelBadge,
  Loading,
  Pill,
  StatTile,
} from '../components/ui';
import { useApi } from '../lib/useApi';
import { age, money, number, pricingLabel, stageLabel, stageTone } from '../lib/format';
import type { ApplicationListItem, Paged, PipelineSummary } from '../lib/types';

/**
 * The underwriter's queue. Same records as the pipeline, ordered by what is
 * actually waiting on a decision rather than by the agent's own workload.
 */
export function Underwriting() {
  const queue = useApi<Paged<ApplicationListItem>>(
    '/applications?filter=in_underwriting&limit=50',
  );
  const conditions = useApi<Paged<ApplicationListItem>>(
    '/applications?stage=conditions&limit=50',
  );
  const exceptions = useApi<Paged<ApplicationListItem>>(
    '/applications?stage=rate_exception&limit=50',
  );
  const summary = useApi<PipelineSummary>('/applications/summary');

  const loading = queue.loading || conditions.loading || exceptions.loading;
  const error = queue.error ?? conditions.error ?? exceptions.error;

  return (
    <>
      <PageHeader breadcrumb={['Underwriting']} title="Underwriting queue" search />

      <ScopeBar>
        Files awaiting a decision. A decline reason you record stays visible to L2 and L3
        only — the writing agent never sees it.
      </ScopeBar>

      <PageBody>
        {loading && <Loading label="Loading the queue…" />}
        {error && <ErrorState error={error} onRetry={queue.reload} />}

        {!loading && !error && (
          <>
            <div className="tilerow">
              <StatTile
                label="With you now"
                value={number(queue.data?.total ?? 0)}
                footnote="Submitted and awaiting a decision"
              />
              <StatTile
                label="Conditions outstanding"
                value={number(conditions.data?.total ?? 0)}
                badge={conditions.data?.total ? 'waiting on agents' : undefined}
                badgeTone="warn"
                footnote="Returned to the agent for documents"
              />
              <StatTile
                label="Rate exceptions"
                value={number(exceptions.data?.total ?? 0)}
                footnote="Below floor — parked with a parent office"
              />
              <StatTile
                label="Open across the book"
                value={number(summary.data?.openTotal ?? 0)}
                footnote={`Rolling ${summary.data?.windowDays ?? 90} days`}
              />
            </div>

            <Queue
              title="Awaiting your decision"
              items={queue.data?.items ?? []}
              empty="Nothing is waiting on underwriting."
            />
            <Queue
              title="Conditions outstanding"
              items={conditions.data?.items ?? []}
              empty="No file is sitting on conditions."
            />
            <Queue
              title="Rate exceptions"
              items={exceptions.data?.items ?? []}
              empty="No pricing exception is pending."
            />
          </>
        )}
      </PageBody>
    </>
  );
}

function Queue({
  title,
  items,
  empty,
}: {
  title: string;
  items: ApplicationListItem[];
  empty: string;
}) {
  return (
    <Card>
      <CardHeader title={title} count={items.length} />
      {items.length === 0 ? (
        <Empty title={empty} />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Merchant</th>
                <th>Submitted by</th>
                <th>Stage</th>
                <th className="num">Monthly vol.</th>
                <th>Pricing</th>
                <th className="num">Age</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id}>
                  <td>
                    <div className="cell-primary">{a.dba}</div>
                    <div className="cell-sub">
                      {a.reference} · MCC {a.mcc}
                    </div>
                  </td>
                  <td>
                    <span className="cell-person">
                      {a.submittedByName}
                      <LevelBadge level={a.submittedByLevel} />
                    </span>
                  </td>
                  <td>
                    <Pill tone={stageTone(a.stage)}>
                      {a.blockedOn ?? stageLabel(a.stage)}
                    </Pill>
                  </td>
                  <td className="num">{money(a.monthlyVolumeCents)}</td>
                  <td>{pricingLabel(a.pricing.model, a.pricing.basisPoints)}</td>
                  <td className="num">{age(a.lastStageChangeAt)}</td>
                  <td>
                    <Link to={`/applications/${a.id}`}>
                      <Button size="sm" variant="primary">
                        Review
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
